import type { StoreApi } from 'zustand'
import type { AIProviderId } from '@creanizer/types'
import {
  getApiKeyProvider,
  indexedDBManager,
  type ApiKey,
} from '@creanizer/utils'
import type { SettingsState } from './settingsStoreTypes'

type SetState = StoreApi<SettingsState>['setState']
type GetState = StoreApi<SettingsState>['getState']

function providerKeys(apiKeys: ApiKey[], provider: AIProviderId): ApiKey[] {
  return apiKeys.filter(key => getApiKeyProvider(key) === provider)
}

export function createApiKeyActions(set: SetState, get: GetState) {
  return {
    addApiKey: async (
      name: string,
      key: string,
      provider = get().selectedProvider
    ) => {
      const keys = providerKeys(get().apiKeys, provider)
      const newApiKey = await indexedDBManager.addApiKey({
        name,
        key,
        provider,
        isActive: keys.length === 0,
      })
      set(state => ({
        apiKeys: [...state.apiKeys, newApiKey],
        activeApiKey: provider === state.selectedProvider && keys.length === 0
          ? newApiKey
          : state.activeApiKey,
      }))
    },

    deleteApiKey: async (id: string) => {
      const target = get().apiKeys.find(key => key.id === id)
      if (!target) return
      const provider = getApiKeyProvider(target)
      await indexedDBManager.deleteApiKey(id)
      const remaining = get().apiKeys.filter(key => key.id !== id)
      let nextActive = providerKeys(remaining, provider)
        .find(key => key.isActive) ?? null

      if (target.isActive && !nextActive) {
        nextActive = providerKeys(remaining, provider)[0] ?? null
        if (nextActive) await indexedDBManager.setActiveApiKey(nextActive.id)
      }

      set(state => ({
        apiKeys: remaining.map(key =>
          nextActive && key.id === nextActive.id ? { ...key, isActive: true } : key
        ),
        activeApiKey: provider === state.selectedProvider
          ? nextActive
          : state.activeApiKey,
      }))
    },

    setActiveApiKey: async (id: string) => {
      const target = get().apiKeys.find(key => key.id === id)
      if (!target) throw new Error('APIキーが見つかりません')
      const provider = getApiKeyProvider(target)
      await indexedDBManager.setActiveApiKey(id)
      set(state => ({
        apiKeys: state.apiKeys.map(key =>
          getApiKeyProvider(key) === provider
            ? { ...key, isActive: key.id === id }
            : key
        ),
        activeApiKey: provider === state.selectedProvider
          ? { ...target, isActive: true }
          : state.activeApiKey,
      }))
    },

    updateApiKey: async (id: string, updates: Partial<ApiKey>) => {
      await indexedDBManager.updateApiKey(id, updates)
      set(state => ({
        apiKeys: state.apiKeys.map(key =>
          key.id === id ? { ...key, ...updates } : key
        ),
        activeApiKey: state.activeApiKey?.id === id
          ? { ...state.activeApiKey, ...updates }
          : state.activeApiKey,
      }))
    },

    setRateLimited: async (id: string, until: Date) => {
      await get().updateApiKey(id, { rateLimitedUntil: until })
    },

    getNextAvailableApiKey: () => {
      const now = new Date()
      return providerKeys(get().apiKeys, get().selectedProvider).find(key =>
        !key.rateLimitedUntil || new Date(key.rateLimitedUntil) < now
      ) ?? null
    },

    getApiKeysForProvider: (provider = get().selectedProvider) =>
      providerKeys(get().apiKeys, provider),

    getActiveApiKeyForProvider: (provider: AIProviderId) => {
      const keys = providerKeys(get().apiKeys, provider)
      return keys.find(key => key.isActive) ?? keys[0] ?? null
    },

    incrementUsage: async (id: string) => {
      try {
        await indexedDBManager.incrementApiKeyUsage(id)
        set(state => ({
          apiKeys: state.apiKeys.map(key => key.id === id
            ? { ...key, usageCount: key.usageCount + 1, lastUsedAt: new Date() }
            : key
          ),
        }))
      } catch (error) {
        console.error('Failed to increment API key usage:', error)
      }
    },

    isApiKeySet: (provider = get().selectedProvider) =>
      providerKeys(get().apiKeys, provider).length > 0,

    clearGeminiApiKey: async () => {
      const active = get().getActiveApiKeyForProvider('gemini')
      if (active) await get().deleteApiKey(active.id)
    },
  }
}
